import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {BookmarkIcon, PlusIcon} from '@heroicons/react/24/outline'
import {cn} from '../../../../../../lib/utils.ts'
import {fieldVariants} from '../../../../../../components/general/fieldStyles.ts'
import {CatalogItem} from '../../../../../../types/catalogue.ts'

const MAX_SUGGESTIONS = 6
const MENU_GAP_PX = 4

type LineDescriptionFieldProps = {
    value: string
    catalogItems: CatalogItem[]
    saveToCatalogue: boolean
    onDescriptionChange: (description: string) => void
    onPickCatalogItem: (item: CatalogItem) => void
    onToggleSaveToCatalogue: (saveToCatalogue: boolean) => void
}

function matchingItems(catalogItems: CatalogItem[], typed: string): CatalogItem[] {
    const needle = typed.trim().toLowerCase()
    if (!needle) return []

    return catalogItems
        .filter(item => item.name.toLowerCase().includes(needle))
        .slice(0, MAX_SUGGESTIONS)
}

function isExistingName(catalogItems: CatalogItem[], typed: string): boolean {
    const needle = typed.trim().toLowerCase()

    return catalogItems.some(item => item.name.trim().toLowerCase() === needle)
}

export function LineDescriptionField({
    value,
    catalogItems,
    saveToCatalogue,
    onDescriptionChange,
    onPickCatalogItem,
    onToggleSaveToCatalogue,
}: LineDescriptionFieldProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [menuRect, setMenuRect] = useState<{top: number, left: number, width: number} | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)

    const suggestions = matchingItems(catalogItems, value)
    const canRemember = value.trim().length > 0 && !isExistingName(catalogItems, value)
    const hasMenu = isOpen && (suggestions.length > 0 || canRemember)

    // The line table scrolls horizontally, and an absolutely positioned menu inside that
    // container gets clipped by it and adds a scrollbar. The menu is therefore portalled to
    // the body and positioned against the input's viewport rect instead.
    const positionMenu = useCallback(() => {
        const input = inputRef.current
        if (!input) return

        const rect = input.getBoundingClientRect()
        setMenuRect({top: rect.bottom + MENU_GAP_PX, left: rect.left, width: rect.width})
    }, [])

    useLayoutEffect(() => {
        if (!hasMenu) return

        positionMenu()
        window.addEventListener('scroll', positionMenu, true)
        window.addEventListener('resize', positionMenu)

        return () => {
            window.removeEventListener('scroll', positionMenu, true)
            window.removeEventListener('resize', positionMenu)
        }
    }, [hasMenu, positionMenu])

    useEffect(() => {
        function onPointerDown(event: MouseEvent) {
            const target = event.target as Node
            if (inputRef.current?.contains(target) || menuRef.current?.contains(target)) return
            setIsOpen(false)
        }

        document.addEventListener('mousedown', onPointerDown)
        return () => document.removeEventListener('mousedown', onPointerDown)
    }, [])

    return (
        <>
            <input
                ref={inputRef}
                className={cn(fieldVariants())}
                value={value}
                onChange={(e) => {
                    onDescriptionChange(e.target.value)
                    setIsOpen(true)
                }}
                onFocus={() => setIsOpen(true)}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') setIsOpen(false)
                }}
                placeholder="Bijv. Webdesign diensten"
                role="combobox"
                aria-expanded={hasMenu}
                aria-controls="line-description-suggestions"
            />

            {saveToCatalogue && (
                <button
                    type="button"
                    onClick={() => onToggleSaveToCatalogue(false)}
                    className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    title="Niet bewaren in je catalogus"
                >
                    <BookmarkIcon className="h-3.5 w-3.5"/>
                    Wordt bewaard in je catalogus
                </button>
            )}

            {hasMenu && menuRect && createPortal(
                <div
                    ref={menuRef}
                    id="line-description-suggestions"
                    role="listbox"
                    style={{
                        position: 'fixed',
                        top: menuRect.top,
                        left: menuRect.left,
                        width: Math.max(menuRect.width, 280),
                    }}
                    className="z-50 rounded-lg border border-gray-200 bg-white shadow-lg"
                >
                    {suggestions.length > 0 && (
                        <>
                            <p className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Voorgesteld
                            </p>
                            <ul className="border-b border-gray-100 pb-1">
                                {suggestions.map(item => (
                                    <li key={item.id}>
                                        <button
                                            type="button"
                                            role="option"
                                            aria-selected={false}
                                            onClick={() => {
                                                onPickCatalogItem(item)
                                                setIsOpen(false)
                                            }}
                                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                                        >
                                            {item.name}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}

                    {canRemember && (
                        <button
                            type="button"
                            onClick={() => {
                                onToggleSaveToCatalogue(true)
                                setIsOpen(false)
                            }}
                            className="flex w-full items-start gap-2 px-3 py-3 text-left text-sm hover:bg-gray-50"
                        >
                            <PlusIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary"/>
                            <span className="text-gray-700">
                                <span className="font-medium">Nieuw</span>
                                {' — '}
                                <span className="font-medium">&ldquo;{value.trim()}&rdquo;</span>
                                {' wordt na versturen bewaard in je catalogus.'}
                            </span>
                        </button>
                    )}
                </div>,
                document.body,
            )}
        </>
    )
}
